import { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Stack, Typography, Alert, Box, CircularProgress,
} from '@mui/material';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import { useOpenTerminalSession } from '../../api/queries';
import { sanitizeTerminalPaste } from '../../utils/terminalPaste';

type Row = { id: string; qrId?: string | null; siteDisplayName?: string | null } | null;

type Phase = 'reason' | 'connecting' | 'open' | 'closed' | 'error';

/**
 * Permission-gated remote shell into a site controller. Two phases: a
 * reason prompt (audited) → an xterm.js terminal bridged over a WebSocket to
 * the cloud relay. Every session is recorded (banner). The shell runs as the
 * SC's admin user (sudo available); no password is ever shown or entered.
 */
export function TerminalDialog({ row, onClose }: { row: Row; onClose: () => void }) {
  const open = row !== null;
  const [phase, setPhase] = useState<Phase>('reason');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const termHostRef = useRef<HTMLDivElement | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const termRef = useRef<Terminal | null>(null);
  const openSession = useOpenTerminalSession(row?.id ?? '00000000-0000-0000-0000-000000000000');

  // Reset when the dialog target changes.
  useEffect(() => {
    if (open) { setPhase('reason'); setReason(''); setError(null); }
  }, [open, row?.id]);

  const teardown = () => {
    try { wsRef.current?.close(); } catch { /* ignore */ }
    wsRef.current = null;
    try { termRef.current?.dispose(); } catch { /* ignore */ }
    termRef.current = null;
  };

  const close = () => { teardown(); onClose(); };

  const connect = () => {
    setError(null);
    openSession.mutate({ reason: reason.trim() }, {
      onSuccess: (res) => {
        setPhase('connecting');
        // Defer until the terminal host div is mounted.
        requestAnimationFrame(() => startTerminal(res.relay_path, res.browser_token));
      },
      onError: (e) => setError((e as { message?: string }).message ?? 'Failed to open session.'),
    });
  };

  const startTerminal = (relayPath: string, browserToken: string) => {
    const host = termHostRef.current;
    if (!host) return;
    const term = new Terminal({
      convertEol: false, cursorBlink: true, fontSize: 13,
      fontFamily: 'Menlo, Consolas, "DejaVu Sans Mono", monospace',
      theme: { background: '#0b0f14' },
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(host);
    fit.fit();
    termRef.current = term;

    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${proto}//${window.location.host}${relayPath}?token=${encodeURIComponent(browserToken)}`;
    const ws = new WebSocket(url);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;
    const enc = new TextEncoder();

    ws.onopen = () => {
      setPhase('open');
      term.writeln('\x1b[2m— connecting to site controller… (the SC dials in on its next heartbeat) —\x1b[0m');
      term.focus();
    };
    ws.onmessage = (ev) => {
      if (typeof ev.data === 'string') term.write(ev.data);
      else term.write(new Uint8Array(ev.data));
    };
    ws.onerror = () => { setError('Terminal connection error.'); setPhase('error'); };
    ws.onclose = () => { setPhase('closed'); term.writeln('\r\n\x1b[2m— session closed —\x1b[0m'); };

    term.onData((d) => { if (ws.readyState === WebSocket.OPEN) ws.send(enc.encode(d)); });

    // Paste must insert PLAIN TEXT ONLY — no ANSI/SGR colour, no bracketed-paste
    // wrappers, no control bytes. attachCustomKeyEventHandler does NOT fire on
    // paste, so intercept the DOM paste event on xterm's textarea in the CAPTURE
    // phase and stop xterm's own handler (which would otherwise add \x1b[200~ /
    // \x1b[201~ wrappers). textarea exists after term.open().
    term.textarea?.addEventListener('paste', (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      const raw = e.clipboardData?.getData('text/plain') ?? '';
      const clean = sanitizeTerminalPaste(raw);
      if (clean && ws.readyState === WebSocket.OPEN) ws.send(enc.encode(clean));
    }, true /* capture phase */);

    const ro = new ResizeObserver(() => { try { fit.fit(); } catch { /* ignore */ } });
    ro.observe(host);
  };

  useEffect(() => () => teardown(), []);

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth={phase === 'reason' ? 'sm' : 'lg'}>
      <DialogTitle>Remote terminal{row?.qrId ? ` — ${row.qrId}` : ''}</DialogTitle>
      <DialogContent>
        {phase === 'reason' && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              This opens a recorded administrative shell on the site controller. All
              activity is logged and the full session is recorded for audit.
            </Alert>
            {row?.siteDisplayName && (
              <Typography variant="body2">Site: {row.siteDisplayName}</Typography>
            )}
            <TextField
              label="Reason (required, audited)" value={reason} autoFocus
              onChange={(e) => setReason(e.target.value)} fullWidth multiline rows={2}
              disabled={openSession.isPending} />
            {error && <Typography color="error" variant="body2">{error}</Typography>}
          </Stack>
        )}
        {phase !== 'reason' && (
          <Stack spacing={1} sx={{ mt: 1 }}>
            <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
              🔴 This session is being recorded.
              {phase === 'connecting' && <> <CircularProgress size={12} sx={{ ml: 1 }} /> waiting for the SC to dial in…</>}
              {phase === 'closed' && ' — session closed.'}
            </Alert>
            {error && <Alert severity="error">{error}</Alert>}
            <Box ref={termHostRef} sx={{ height: 460, width: '100%', bgcolor: '#0b0f14', borderRadius: 1, p: 1 }} />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {phase === 'reason' ? (
          <>
            <Button onClick={close} disabled={openSession.isPending}>Cancel</Button>
            <Button onClick={connect} variant="contained"
                    disabled={openSession.isPending || reason.trim().length === 0}>
              {openSession.isPending ? 'Opening…' : 'Open terminal'}
            </Button>
          </>
        ) : (
          <Button onClick={close} variant="contained">Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
